import { Component } from 'react';
import { Navigate } from 'react-router-dom';

import { 
  ThemeProvider, 
  createTheme, 
} from '@mui/material/styles';
import {
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  // Alert,
} from '@mui/material';

// import { HttpStatusCode } from 'axios';

import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';

import { withTranslation } from 'react-i18next';
// import i18n from '../assets/locales';

import Layout from '../layouts/layout.component';
import UserService from '../services/user.service';

type Props = {};

type State = {
  redirect: string | null,
  uuid: string,
  token: string,
  username: string,
  expired: boolean
  loading: boolean,
  success: boolean,
  message: string
};

const theme = createTheme({
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontSize: 13,
          fontWeight: 'bold',
          color: 'rgba(224, 224, 224, 0.98)' ,
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          // Standard
          '& .MuiInput-root': {
            color: 'rgba(255, 255, 255, 0.8)',
            // fontFamily: 'Arial',
            fontWeight: 'bold',
            '&:before': {
              borderColor: 'rgba(128, 128, 128, 0.8)',
              borderWidth: '1px',
            },
            '&:after': {
              borderColor: 'rgba(0, 98, 65, 0.8)',
              borderWidth: '2px',
            },
            ':hover:not(.Mui-focused)': {
              '&:before': {
                borderColor: 'rgba(231, 231, 231, 0.8)',
                borderWidth: '1px',
              },
            },
          },
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          '& .MuiAlert-message': {
            color: 'rgba(0, 0, 0, 1)'
          }
        }
      }
    }
  }
});

class ResetPassword extends Component<Props, State> {
  private params: any;

  constructor(props: Props) {
    super(props);

    this.onTryAgain = this.onTryAgain.bind(this);
    this.handleChangePassword = this.handleChangePassword.bind(this);

    this.state = {
      redirect: null,
      uuid: '',
      token: '',
      username: '',
      expired: false,
      loading: false,
      success: false,
      message: ''
    };
  }

  componentDidMount() {
    const url = window.location.href;
    const urlParts = url.split('/');
    const uuid = urlParts[urlParts.length - 2];
    const token = urlParts[urlParts.length - 1];

    // console.log(`UUID: ${uuid}, Token: ${token}`);

    UserService.checkPasswordResetTokenValidity(uuid, token)
      .then(
        (res) => {
          console.log(`Token validity: ${JSON.stringify(res, null, 2)}`);
          this.setState({
            uuid: uuid,
            token: token,
            expired: false 
          });
        },
        (err) => {
          console.error(`Error checking token validity: ${err}`);
          this.setState({ expired: true });
        }
      )
    // const currentUser = AuthService.getCurrentUser();

    // if (currentUser) {
    //   this.setState({ redirect: '/profile' });
    // };
  }

  componentWillUnmount() {
    window.location.reload();
  }

  onTryAgain() {
    console.log('Try again clicked');

    this.setState({
      redirect: '/account/forgot-password'
     });
  }

  validateSchema() {
    return Yup.object().shape({
      password: Yup.string()
        .required('This field is required!')
        .min(8, 'Password must be at least 8 characters')
        .max(20, 'Password must be at most 20 characters'),
    });
  }
  
  handleChangePassword(values: { password: string }) {
    const { password } = values;

    console.log(`UUID: ${this.state.uuid}, Token: ${this.state.token}`);
    console.log(`New password: ${password}`);

    UserService.resetPassword(this.state.uuid, this.state.token, password).then(
      (res) => {
        console.log(`Password reset successful: ${JSON.stringify(res, null, 2)}`);

        this.setState({
          loading: false,
          success: true,
          message: res.data.message,
        });

        setTimeout(() => {
          this.setState({
            redirect: '/signin'
          });
        }, 3000);
      },
      (err) => {
        console.error(`Password reset failed: ${JSON.stringify(err, null, 2)}`);

        let message = 
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          'An error occurred while resetting password. Please try again later.';

        this.setState({
          loading: false,
          success: false,
          message: message,
        });
      }
    );
  }

  render() {
    if (this.state.redirect) {
      return <Navigate to={this.state.redirect} />
    }

    const expired = this.state.expired;

    return(
      <Layout>
        <div className='container col-md-12'>
          <Box sx={{ height: 10 }} />
            {
              expired ? ( 
                <>
                <div className='container-fluid'>
                  <Typography variant='h5'><b>Reset password link expired</b></Typography>
                </div>
                <Box sx={{ height: 20 }} />
                <div className='container-fluid'>
                  <p className='fs-3 text-description'>
                    {/* { i18n.t('signin:description') } */}
                    If you'd still like to reset your password, we can send you another link.
                  </p>
                </div>
                <Box sx={{ height: 20 }} />
                <div className='container card card-container' style={{background: 'transparent'}}>
                  <Button className='btn btn-primary btn-block w-100' onClick={this.onTryAgain}>
                    Try again
                  </Button>
                </div>
                </>
              ) : (
                <>
                <div className='container-fluid'>
                  <Typography variant='h5'><b>Create new password</b></Typography>
                </div>
                <Box sx={{ height: 20 }} />
                <div className='container-fluid'>
                  <p className='fs-3 text-description'>
                    Enter your new password for your SquareGolf account.
                  </p>
                </div>
                <Box sx={{ height: 20 }} />
                <div className='container card card-container' style={{background: 'transparent'}}>
                  <ThemeProvider theme={theme}>
                    <Formik initialValues={{ password: '' }} 
                            validationSchema={this.validateSchema} 
                            onSubmit={this.handleChangePassword}>
                      {(props) => (
                      <Form>
                        <FormControl fullWidth>
                          <Typography variant='subtitle1'>
                            Password <sup style={{ color: 'rgb(224, 0, 0, 1)' }}>*</sup>
                          </Typography>
                          <Field as={TextField}
                                type='password'
                                name='password'
                                variant='standard'
                                placeholder='Password' />
                          <ErrorMessage component='div' 
                                        name='password' 
                                        className='alert alert-danger' />
                        </FormControl>
                        <Box sx={{ height: 20 }} />
                        <Button type='submit' className='btn btn-primary btn-block w-100'>
                          Change password
                        </Button>
                      </Form>
                      )}
                    </Formik>
                  </ThemeProvider>
                </div>
                </>
              )
            }
        </div>
      </Layout>
    )
  }
}

export default withTranslation()(ResetPassword)

